/** Operator console shapes (05 §5 `/op/*`). Kept apart from the homeowner types because these
 *  carry decrypted PII and the private final price — a shared module is how that leaks into a
 *  homeowner bundle. */

import { BUSINESS_TIMEZONE } from './datetime'

export interface InboxItem {
  id: string
  status: string
  categories: string[]
  created_at: string
  homeowner_name: string | null
  town: string | null
  in_service_area: boolean | null
  workspace_declined: boolean
  photo_count: number
  booking_start: string | null
  booking_status: string | null
  outcome: string | null
}

export interface InboxPage {
  items: InboxItem[]
  next_cursor: string | null
  counts: Record<string, number>
}

interface OperatorBooking {
  id: string
  slot_start: string
  slot_end: string
  status: string
  confirmed_at: string | null
  rescheduled_from: string | null
  reminder_sent_at: string | null
}

export interface RequestDetail {
  id: string
  status: string
  created_at: string
  categories: string[]
  description: string | null
  contact_pref: string
  workspace_declined: boolean
  in_service_area: boolean | null
  homeowner_name: string | null
  homeowner_email: string
  homeowner_phone: string | null
  address_line1: string | null
  address_line2: string | null
  city: string | null
  state: string | null
  zip: string | null
  lat: number | null
  lon: number | null
  photos: { id: string; url: string; width: number | null; height: number | null }[]
  booking: OperatorBooking | null
  outcome_status: 'quoted' | 'won' | 'lost' | null
  outcome_final_price: string | null
  outcome_notes: string | null
  workspace: WorkspaceSummary | null
}

/** The design session, as the operator console shows it (01 §4, WS7-06). */
export interface WorkspaceSummary {
  session_id: string
  status: 'active' | 'sent' | 'expired'
  sent_at: string | null
  lines: {
    room: string
    slot: string | null
    what: string
    qty: string
    measured: boolean
    material_price: string | null
    price_as_of: string | null
    store: string | null
    url: string | null
    labor_from: string
  }[]
  must_verify: string[]
  open_questions: string[]
  renders: {
    id: string
    room: string
    url: string | null
    thumb_url: string | null
    created_at: string
    low_confidence: boolean
    direction: string | null
  }[]
  transcript: { role: string; content: string }[]
  packet_id: string | null
  packet_pdf_url: string | null
}

export interface Rule {
  id?: string
  weekday: number
  start_time: string
  end_time: string
  visit_minutes: number
  buffer_minutes: number
  effective_from?: string | null
  effective_to?: string | null
  enabled: boolean
  note?: string | null
}

export interface Blackout {
  id: string
  start_ts: string
  end_ts: string
  reason: string | null
}

export interface AvailabilitySettings {
  id?: string
  lead_time_hours: number
  max_horizon_days: number
  hold_minutes: number
  timezone: string
}

export interface BookingRow {
  id: string
  estimate_request_id: string
  slot_start: string
  slot_end: string
  status: string
  homeowner_name: string | null
  homeowner_phone: string | null
  town: string | null
  categories: string[]
}

export const WEEKDAYS = [
  'Monday',
  'Tuesday',
  'Wednesday',
  'Thursday',
  'Friday',
  'Saturday',
  'Sunday',
]

/**
 * The business runs in one timezone (10 §3) and it is stored on `availability_setting`.
 * Used as the fallback while that setting is loading; never as a substitute for it.
 */
export const DEFAULT_TIMEZONE = BUSINESS_TIMEZONE

// Formatting lives in one module, where the zone is a required argument. See lib/datetime.ts
// for the bug that put it there.
export { shortDate, shortDateTime } from './datetime'
